import React, { useState } from 'react';
import { motion } from 'motion/react';
import { romanticAudio } from '../utils/audio';

interface GrandFloralBouquetProps {
  recipientName?: string;
  senderName?: string;
  onInteract?: () => void;
}

export const GrandFloralBouquet: React.FC<GrandFloralBouquetProps> = ({
  recipientName,
  senderName,
  onInteract,
}) => {
  const [tapSpark, setTapSpark] = useState<boolean>(false);

  const handleTap = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTapSpark(true);

    // Play sweet celestial harp arpeggio
    romanticAudio.playNote(523.25, 0.7, 'sine', 0.1); // C5
    setTimeout(() => romanticAudio.playNote(659.25, 0.7, 'sine', 0.12), 90); // E5
    setTimeout(() => romanticAudio.playNote(783.99, 0.8, 'sine', 0.14), 180); // G5
    setTimeout(() => romanticAudio.playNote(1046.5, 0.9, 'triangle', 0.22), 290); // C6

    if (onInteract) onInteract();
    setTimeout(() => setTapSpark(false), 700);
  };

  return (
    <div
      onClick={handleTap}
      className="relative w-full max-w-sm sm:max-w-md mx-auto flex flex-col items-center justify-center cursor-pointer select-none py-1 group"
      title="Toca el ramo para iluminarlo ✨"
    >
      {/* Radiant Ambient Candlelight Glow behind the bouquet */}
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-72 h-72 bg-gradient-to-b from-amber-300/30 via-yellow-500/20 to-transparent rounded-full blur-3xl pointer-events-none group-hover:from-amber-300/45 transition-all duration-700" />
      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-44 h-44 bg-yellow-200/25 rounded-full blur-2xl pointer-events-none" />

      {/* Floating Swaying Bouquet Container */}
      <motion.div
        className="w-full relative flex items-center justify-center"
        animate={{
          y: [0, -6, 0],
          rotate: [0, 0.6, -0.6, 0],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.98 }}
      >
        <svg
          viewBox="0 0 600 700"
          className="w-full h-auto max-h-[380px] sm:max-h-[440px] drop-shadow-[0_16px_35px_rgba(0,0,0,0.65)] overflow-visible"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/* Background Halo Glow Filter */}
            <filter id="softGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="8" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>

            {/* Radial Bouquet Aura */}
            <radialGradient id="auroraGlow" cx="50%" cy="32%" r="45%">
              <stop offset="0%" stopColor="#FFFDE7" stopOpacity="0.8" />
              <stop offset="30%" stopColor="#FDE047" stopOpacity="0.5" />
              <stop offset="70%" stopColor="#F59E0B" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
            </radialGradient>

            {/* --- TULIP GRADIENTS --- */}
            {/* Tulip Center Gold */}
            <linearGradient id="tulipGoldCore" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#FFFBEB" />
              <stop offset="25%" stopColor="#FEF08A" />
              <stop offset="70%" stopColor="#FACC15" />
              <stop offset="100%" stopColor="#D97706" />
            </linearGradient>

            {/* Tulip Left & Right Shaded Petal */}
            <linearGradient id="tulipPetalShade" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FEF9C3" />
              <stop offset="40%" stopColor="#FDE047" />
              <stop offset="85%" stopColor="#EAB308" />
              <stop offset="100%" stopColor="#B45309" />
            </linearGradient>

            {/* Tulip Peach Gold Outer */}
            <linearGradient id="tulipPeachSoft" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FFF1F2" />
              <stop offset="35%" stopColor="#FDE68A" />
              <stop offset="75%" stopColor="#FB923C" />
              <stop offset="100%" stopColor="#EA580C" />
            </linearGradient>

            {/* --- LILY (LIRIO) GRADIENTS --- */}
            {/* Lily Outer Petals - Radiant Golden Cream */}
            <linearGradient id="lilyPetalGrad" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="20%" stopColor="#FFFBEB" />
              <stop offset="60%" stopColor="#FEF08A" />
              <stop offset="90%" stopColor="#FBBF24" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>

            {/* Lily Stamen Filament */}
            <linearGradient id="lilyFilament" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#D9F99D" />
              <stop offset="70%" stopColor="#84CC16" />
              <stop offset="100%" stopColor="#4D7C0F" />
            </linearGradient>

            {/* --- SUNFLOWER (GIRASOL) GRADIENTS --- */}
            {/* Sunflower Golden Petals */}
            <linearGradient id="sunPetalPrimary" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#FFFDE7" />
              <stop offset="20%" stopColor="#FDE047" />
              <stop offset="75%" stopColor="#F59E0B" />
              <stop offset="100%" stopColor="#B45309" />
            </linearGradient>

            <linearGradient id="sunPetalBack" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#FEF08A" />
              <stop offset="50%" stopColor="#F59E0B" />
              <stop offset="100%" stopColor="#92400E" />
            </linearGradient>

            {/* Sunflower Rich Center Seed Disc */}
            <radialGradient id="sunSeedCenter" cx="45%" cy="40%" r="55%">
              <stop offset="0%" stopColor="#78350F" />
              <stop offset="45%" stopColor="#451A03" />
              <stop offset="85%" stopColor="#271003" />
              <stop offset="100%" stopColor="#170802" />
            </radialGradient>

            {/* --- WRAPPER PAPER GRADIENTS --- */}
            {/* Outer Translucent Paper Wings */}
            <linearGradient id="wrapWingLeft" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
              <stop offset="40%" stopColor="#FDF2F8" stopOpacity="0.9" />
              <stop offset="80%" stopColor="#F1F5F9" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#E2E8F0" stopOpacity="0.8" />
            </linearGradient>

            <linearGradient id="wrapWingRight" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
              <stop offset="40%" stopColor="#FDF2F8" stopOpacity="0.9" />
              <stop offset="80%" stopColor="#F1F5F9" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#E2E8F0" stopOpacity="0.8" />
            </linearGradient>

            {/* Front Origami Cone Paper */}
            <linearGradient id="wrapFrontCone" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.98" />
              <stop offset="35%" stopColor="#FFF1F2" stopOpacity="0.92" />
              <stop offset="70%" stopColor="#F8FAFC" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#E2E8F0" stopOpacity="0.85" />
            </linearGradient>

            <linearGradient id="wrapInnerShadow" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#0F172A" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#0F172A" stopOpacity="0" />
            </linearGradient>

            {/* --- RIBBON PINK SATIN GRADIENTS --- */}
            <linearGradient id="ribbonSatin" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FFF1F2" />
              <stop offset="30%" stopColor="#F472B6" />
              <stop offset="75%" stopColor="#DB2777" />
              <stop offset="100%" stopColor="#9D174D" />
            </linearGradient>

            {/* --- LEAF & STEM GRADIENTS --- */}
            <linearGradient id="stemGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#84CC16" />
              <stop offset="50%" stopColor="#4D7C0F" />
              <stop offset="100%" stopColor="#365314" />
            </linearGradient>

            <linearGradient id="leafGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#A3E635" />
              <stop offset="50%" stopColor="#65A30D" />
              <stop offset="100%" stopColor="#3F6212" />
            </linearGradient>
          </defs>

          {/* 1. SOFT GOLDEN ATMOSPHERIC AURA */}
          <circle cx="300" cy="230" r="190" fill="url(#auroraGlow)" />

          {/* 2. BACK WINGS OF ARTISANAL WRAPPING PAPER */}
          <g id="wrapper-backdrop">
            {/* Left Origami Wing */}
            <polygon
              points="300,560 140,410 170,300 250,380"
              fill="url(#wrapWingLeft)"
              stroke="#FCE7F3"
              strokeWidth="1.5"
            />
            {/* Right Origami Wing */}
            <polygon
              points="300,560 460,410 430,300 350,380"
              fill="url(#wrapWingRight)"
              stroke="#FCE7F3"
              strokeWidth="1.5"
            />
            {/* Inner Back Fold Shadow */}
            <polygon
              points="300,560 170,300 230,370 300,385 370,370 430,300"
              fill="url(#wrapInnerShadow)"
            />
          </g>

          {/* 3. BOTANICAL STEMS & FOLIAGE */}
          <g id="stems-and-leaves">
            {/* Stems Fan */}
            <path d="M 300,530 Q 295,380 300,240" stroke="url(#stemGrad)" strokeWidth="6.5" strokeLinecap="round" fill="none" />
            <path d="M 300,530 Q 275,380 240,225" stroke="url(#stemGrad)" strokeWidth="6" strokeLinecap="round" fill="none" />
            <path d="M 300,530 Q 325,380 360,225" stroke="url(#stemGrad)" strokeWidth="6" strokeLinecap="round" fill="none" />
            <path d="M 300,530 Q 240,400 185,270" stroke="url(#stemGrad)" strokeWidth="5.5" strokeLinecap="round" fill="none" />
            <path d="M 300,530 Q 360,400 415,270" stroke="url(#stemGrad)" strokeWidth="5.5" strokeLinecap="round" fill="none" />

            {/* Elegant Curving Tulip Leaves */}
            <path
              d="M 270,440 C 210,400 160,330 175,250 C 205,300 250,350 280,410 Z"
              fill="url(#leafGrad)"
              opacity="0.95"
            />
            <path
              d="M 330,440 C 390,400 440,330 425,250 C 395,300 350,350 320,410 Z"
              fill="url(#leafGrad)"
              opacity="0.95"
            />
            <path
              d="M 285,480 C 235,430 215,360 230,290 C 255,340 280,390 295,450 Z"
              fill="url(#leafGrad)"
              opacity="0.9"
            />
            <path
              d="M 315,480 C 365,430 385,360 370,290 C 345,340 320,390 305,450 Z"
              fill="url(#leafGrad)"
              opacity="0.9"
            />
          </g>

          {/* 4. GYPSOPHILA SPARKLE CLUSTERS (Baby's Breath) */}
          <g id="gypsophila-clusters">
            {[
              { x: 235, y: 260, r: 3.5 },
              { x: 220, y: 245, r: 3 },
              { x: 255, y: 275, r: 3.2 },
              { x: 365, y: 260, r: 3.5 },
              { x: 380, y: 245, r: 3 },
              { x: 345, y: 275, r: 3.2 },
              { x: 300, y: 285, r: 4 },
              { x: 180, y: 290, r: 3 },
              { x: 420, y: 290, r: 3 },
              { x: 210, y: 190, r: 3.5 },
              { x: 390, y: 190, r: 3.5 },
              { x: 300, y: 120, r: 3 },
            ].map((p, idx) => (
              <g key={`gyp-${idx}`}>
                <circle cx={p.x} cy={p.y} r={p.r + 2} fill="#FEF08A" opacity="0.35" />
                <circle cx={p.x} cy={p.y} r={p.r} fill="#FFFFFF" opacity="0.95" />
              </g>
            ))}
          </g>

          {/* 5. TULIPANES (Golden Glowing Tulips at the Top) */}
          <g id="tulips-layer">
            {/* Center Main Tall Tulip */}
            <g transform="translate(300, 155)">
              <ellipse cx="0" cy="-10" rx="42" ry="50" fill="#FDE047" opacity="0.3" filter="blur(8px)" />
              {/* Back Petal Cup */}
              <path
                d="M -30,0 C -30,-55 -15,-72 0,-72 C 15,-72 30,-55 30,0 C 30,30 15,42 0,42 C -15,42 -30,30 -30,0 Z"
                fill="url(#tulipGoldCore)"
              />
              {/* Left Outer Petal Lobe */}
              <path
                d="M -32,-5 C -32,-58 -14,-65 -5,-65 C -5,-30 -10,18 -30,36 C -34,22 -32,10 -32,-5 Z"
                fill="url(#tulipPetalShade)"
              />
              {/* Right Outer Petal Lobe */}
              <path
                d="M 32,-5 C 32,-58 14,-65 5,-65 C 5,-30 10,18 30,36 C 34,22 32,10 32,-5 Z"
                fill="url(#tulipPetalShade)"
              />
              {/* Center Front Petal */}
              <path
                d="M -18,2 C -18,-52 -5,-62 0,-62 C 5,-62 18,-52 18,2 C 18,34 6,43 0,43 C -6,43 -18,34 -18,2 Z"
                fill="url(#tulipGoldCore)"
                stroke="#FEF08A"
                strokeWidth="0.8"
              />
              {/* Dew Highlight */}
              <ellipse cx="-6" cy="-28" rx="2.5" ry="6" fill="#FFFFFF" opacity="0.65" />
            </g>

            {/* Left Tulip (Angled) */}
            <g transform="translate(245, 175) rotate(-16)">
              <ellipse cx="0" cy="-8" rx="36" ry="44" fill="#FDE047" opacity="0.25" filter="blur(6px)" />
              <path
                d="M -26,0 C -26,-50 -12,-65 0,-65 C 12,-65 26,-50 26,0 C 26,26 12,38 0,38 C -12,38 -26,26 -26,0 Z"
                fill="url(#tulipGoldCore)"
              />
              <path
                d="M -28,-4 C -28,-52 -12,-60 -4,-60 C -4,-28 -8,15 -26,32 Z"
                fill="url(#tulipPetalShade)"
              />
              <path
                d="M 28,-4 C 28,-52 12,-60 4,-60 C 4,-28 8,15 26,32 Z"
                fill="url(#tulipPetalShade)"
              />
              <path
                d="M -15,0 C -15,-48 -4,-56 0,-56 C 4,-56 15,-48 15,0 C 15,30 5,39 0,39 C -5,39 -15,30 -15,0 Z"
                fill="url(#tulipGoldCore)"
              />
            </g>

            {/* Right Tulip (Angled) */}
            <g transform="translate(355, 175) rotate(16)">
              <ellipse cx="0" cy="-8" rx="36" ry="44" fill="#FDE047" opacity="0.25" filter="blur(6px)" />
              <path
                d="M -26,0 C -26,-50 -12,-65 0,-65 C 12,-65 26,-50 26,0 C 26,26 12,38 0,38 C -12,38 -26,26 -26,0 Z"
                fill="url(#tulipGoldCore)"
              />
              <path
                d="M -28,-4 C -28,-52 -12,-60 -4,-60 C -4,-28 -8,15 -26,32 Z"
                fill="url(#tulipPetalShade)"
              />
              <path
                d="M 28,-4 C 28,-52 12,-60 4,-60 C 4,-28 8,15 26,32 Z"
                fill="url(#tulipPetalShade)"
              />
              <path
                d="M -15,0 C -15,-48 -4,-56 0,-56 C 4,-56 15,-48 15,0 C 15,30 5,39 0,39 C -5,39 -15,30 -15,0 Z"
                fill="url(#tulipGoldCore)"
              />
            </g>

            {/* Far Left Peach Tulip Bud */}
            <g transform="translate(195, 215) rotate(-30)">
              <path
                d="M -22,0 C -22,-44 -10,-56 0,-56 C 10,-56 22,-44 22,0 C 22,24 10,34 0,34 C -10,34 -22,24 -22,0 Z"
                fill="url(#tulipPeachSoft)"
              />
              <path
                d="M -12,0 C -12,-40 -3,-48 0,-48 C 3,-48 12,-40 12,0 C 12,26 4,35 0,35 C -4,35 -12,26 -12,0 Z"
                fill="url(#tulipGoldCore)"
              />
            </g>

            {/* Far Right Peach Tulip Bud */}
            <g transform="translate(405, 215) rotate(30)">
              <path
                d="M -22,0 C -22,-44 -10,-56 0,-56 C 10,-56 22,-44 22,0 C 22,24 10,34 0,34 C -10,34 -22,24 -22,0 Z"
                fill="url(#tulipPeachSoft)"
              />
              <path
                d="M -12,0 C -12,-40 -3,-48 0,-48 C 3,-48 12,-40 12,0 C 12,26 4,35 0,35 C -4,35 -12,26 -12,0 Z"
                fill="url(#tulipGoldCore)"
              />
            </g>
          </g>

          {/* 6. LIRIOS MAJESTUOSOS (Lilies on Left and Right flanks) */}
          <g id="lilies-layer">
            {/* Left Majestic Lily */}
            <g transform="translate(175, 255)">
              <circle cx="0" cy="0" r="50" fill="#FBBF24" opacity="0.3" filter="blur(8px)" />
              {/* 6 Curving Petals */}
              {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                <g key={`lily-l-${i}`} transform={`rotate(${deg})`}>
                  <path
                    d="M 0,0 C -15,-20 -20,-48 0,-62 C 20,-48 15,-20 0,0 Z"
                    fill="url(#lilyPetalGrad)"
                    stroke="#F59E0B"
                    strokeWidth="0.8"
                  />
                  {/* Petal Central Vein */}
                  <path d="M 0,-6 Q 0,-32 0,-56" stroke="#D97706" strokeWidth="0.9" fill="none" />
                  {/* Throat Freckles / Pollen Spots */}
                  <circle cx="-4" cy="-22" r="1.1" fill="#92400E" />
                  <circle cx="4" cy="-26" r="1" fill="#92400E" />
                  <circle cx="-2" cy="-32" r="0.9" fill="#92400E" />
                  <circle cx="3" cy="-38" r="0.9" fill="#92400E" />
                </g>
              ))}
              {/* Stamens with Pollen Tips */}
              {[-40, -15, 15, 40, -70, 70].map((sDeg, idx) => (
                <g key={`stam-l-${idx}`} transform={`rotate(${sDeg})`}>
                  <line x1="0" y1="0" x2="0" y2="-28" stroke="url(#lilyFilament)" strokeWidth="1.5" />
                  <ellipse cx="0" cy="-29" rx="4" ry="1.8" fill="#78350F" />
                </g>
              ))}
              <circle cx="0" cy="0" r="5.5" fill="#65A30D" />
            </g>

            {/* Right Majestic Lily */}
            <g transform="translate(425, 255)">
              <circle cx="0" cy="0" r="50" fill="#FBBF24" opacity="0.3" filter="blur(8px)" />
              {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                <g key={`lily-r-${i}`} transform={`rotate(${deg})`}>
                  <path
                    d="M 0,0 C -15,-20 -20,-48 0,-62 C 20,-48 15,-20 0,0 Z"
                    fill="url(#lilyPetalGrad)"
                    stroke="#F59E0B"
                    strokeWidth="0.8"
                  />
                  <path d="M 0,-6 Q 0,-32 0,-56" stroke="#D97706" strokeWidth="0.9" fill="none" />
                  <circle cx="-4" cy="-22" r="1.1" fill="#92400E" />
                  <circle cx="4" cy="-26" r="1" fill="#92400E" />
                  <circle cx="-2" cy="-32" r="0.9" fill="#92400E" />
                  <circle cx="3" cy="-38" r="0.9" fill="#92400E" />
                </g>
              ))}
              {[-40, -15, 15, 40, -70, 70].map((sDeg, idx) => (
                <g key={`stam-r-${idx}`} transform={`rotate(${sDeg})`}>
                  <line x1="0" y1="0" x2="0" y2="-28" stroke="url(#lilyFilament)" strokeWidth="1.5" />
                  <ellipse cx="0" cy="-29" rx="4" ry="1.8" fill="#78350F" />
                </g>
              ))}
              <circle cx="0" cy="0" r="5.5" fill="#65A30D" />
            </g>
          </g>

          {/* 7. GIRASOLES (Sunflowers in the Center and Foreground) */}
          <g id="sunflowers-layer">
            {/* Main Central Sunflower */}
            <g transform="translate(300, 270)">
              <circle cx="0" cy="0" r="68" fill="#FACC15" opacity="0.35" filter="blur(10px)" />
              {/* Outer Golden Petals Ring (20 Petals) */}
              {Array.from({ length: 20 }).map((_, i) => {
                const angle = (i * 360) / 20;
                return (
                  <g key={`sun-main-out-${i}`} transform={`rotate(${angle})`}>
                    <path
                      d="M 0,0 C -9,-18 -11,-48 0,-56 C 11,-48 9,-18 0,0 Z"
                      fill="url(#sunPetalPrimary)"
                      stroke="#D97706"
                      strokeWidth="0.6"
                    />
                  </g>
                );
              })}

              {/* Inner Petals Ring (20 Petals, Offset) */}
              {Array.from({ length: 20 }).map((_, i) => {
                const angle = (i * 360) / 20 + 9;
                return (
                  <g key={`sun-main-in-${i}`} transform={`rotate(${angle})`}>
                    <path
                      d="M 0,0 C -7,-15 -10,-40 0,-48 C 10,-40 7,-15 0,0 Z"
                      fill="#FDE047"
                      stroke="#B45309"
                      strokeWidth="0.5"
                    />
                  </g>
                );
              })}

              {/* Dark Velvety Seed Disc */}
              <circle cx="0" cy="0" r="26" fill="url(#sunSeedCenter)" stroke="#92400E" strokeWidth="2" />

              {/* Spiral Golden Pollen Seeds */}
              {Array.from({ length: 32 }).map((_, i) => {
                const rad = 5 + (i % 5) * 4.5;
                const a = i * 2.399;
                const dx = Math.cos(a) * rad;
                const dy = Math.sin(a) * rad;
                return <circle key={`seed-${i}`} cx={dx} cy={dy} r="1.1" fill="#FDE047" opacity="0.75" />;
              })}
            </g>

            {/* Lower-Left Sunflower */}
            <g transform="translate(235, 335) scale(0.85)">
              <circle cx="0" cy="0" r="54" fill="#FACC15" opacity="0.3" filter="blur(8px)" />
              {Array.from({ length: 18 }).map((_, i) => (
                <g key={`sun-ll-${i}`} transform={`rotate(${(i * 360) / 18})`}>
                  <path d="M 0,0 C -8,-16 -10,-44 0,-52 C 10,-44 8,-16 0,0 Z" fill="url(#sunPetalPrimary)" stroke="#D97706" strokeWidth="0.5" />
                </g>
              ))}
              <circle cx="0" cy="0" r="22" fill="url(#sunSeedCenter)" stroke="#92400E" strokeWidth="1.6" />
              {Array.from({ length: 20 }).map((_, i) => {
                const a = i * 2.4;
                return <circle key={`seed-l-${i}`} cx={Math.cos(a) * (4 + (i % 4) * 4)} cy={Math.sin(a) * (4 + (i % 4) * 4)} r="1" fill="#FDE047" opacity="0.7" />;
              })}
            </g>

            {/* Lower-Right Sunflower */}
            <g transform="translate(365, 335) scale(0.85)">
              <circle cx="0" cy="0" r="54" fill="#FACC15" opacity="0.3" filter="blur(8px)" />
              {Array.from({ length: 18 }).map((_, i) => (
                <g key={`sun-lr-${i}`} transform={`rotate(${(i * 360) / 18})`}>
                  <path d="M 0,0 C -8,-16 -10,-44 0,-52 C 10,-44 8,-16 0,0 Z" fill="url(#sunPetalPrimary)" stroke="#D97706" strokeWidth="0.5" />
                </g>
              ))}
              <circle cx="0" cy="0" r="22" fill="url(#sunSeedCenter)" stroke="#92400E" strokeWidth="1.6" />
              {Array.from({ length: 20 }).map((_, i) => {
                const a = i * 2.4;
                return <circle key={`seed-r-${i}`} cx={Math.cos(a) * (4 + (i % 4) * 4)} cy={Math.sin(a) * (4 + (i % 4) * 4)} r="1" fill="#FDE047" opacity="0.7" />;
              })}
            </g>
          </g>

          {/* 8. ARTISAN CRAFT CONE WRAPPER (Foreground Folds) */}
          <g id="wrapper-foreground">
            {/* Left Origami Flap */}
            <polygon
              points="300,610 145,370 190,410 265,450 300,610"
              fill="url(#wrapWingLeft)"
              stroke="#FCE7F3"
              strokeWidth="1.4"
            />
            {/* Right Origami Flap */}
            <polygon
              points="300,610 455,370 410,410 335,450 300,610"
              fill="url(#wrapWingRight)"
              stroke="#FCE7F3"
              strokeWidth="1.4"
            />
            {/* Center Front Crisp Cone Fold */}
            <polygon
              points="300,610 190,410 300,435 410,410"
              fill="url(#wrapFrontCone)"
              stroke="#CBD5E1"
              strokeWidth="1.2"
            />
            {/* Central Vertical Crisp Fold Crease */}
            <path d="M 300,435 L 300,610" stroke="#CBD5E1" strokeWidth="1.4" opacity="0.6" strokeDasharray="3 3" />
            {/* Top Glowing Trim Highlight */}
            <path d="M 190,410 Q 300,438 410,410" stroke="#FFFFFF" strokeWidth="2.5" fill="none" opacity="0.95" />
          </g>

          {/* 9. PINK SATIN RIBBON & ELEGANT BOW AT CONE BASE */}
          <g id="ribbon-bow-layer" transform="translate(300, 600)">
            {/* Flowing Ribbon Tails */}
            <path
              d="M -7,6 C -18,30 -34,60 -40,82 C -28,72 -16,78 -10,65 C -6,42 -3,20 -3,6 Z"
              fill="url(#ribbonSatin)"
              opacity="0.95"
            />
            <path
              d="M 7,6 C 18,30 34,60 40,82 C 28,72 16,78 10,65 C 6,42 3,20 3,6 Z"
              fill="url(#ribbonSatin)"
              opacity="0.95"
            />

            {/* Left Bow Loop */}
            <ellipse
              cx="-24"
              cy="-3"
              rx="22"
              ry="12"
              transform="rotate(-18 -24 -3)"
              fill="url(#ribbonSatin)"
              stroke="#FCE7F3"
              strokeWidth="1.2"
            />
            <ellipse
              cx="-24"
              cy="-3"
              rx="9"
              ry="4"
              transform="rotate(-18 -24 -3)"
              fill="#9D174D"
              opacity="0.45"
            />

            {/* Right Bow Loop */}
            <ellipse
              cx="24"
              cy="-3"
              rx="22"
              ry="12"
              transform="rotate(18 24 -3)"
              fill="url(#ribbonSatin)"
              stroke="#FCE7F3"
              strokeWidth="1.2"
            />
            <ellipse
              cx="24"
              cy="-3"
              rx="9"
              ry="4"
              transform="rotate(18 24 -3)"
              fill="#9D174D"
              opacity="0.45"
            />

            {/* Central Knot */}
            <circle cx="0" cy="0" r="9" fill="url(#ribbonSatin)" stroke="#FCE7F3" strokeWidth="1.4" />
            <ellipse cx="-2.5" cy="-2.5" rx="2.5" ry="4" fill="#FFFFFF" opacity="0.65" />
          </g>
        </svg>
      </motion.div>

      {/* Romantic Glow Subtitle */}
      <motion.p
        animate={{ opacity: [0.75, 1, 0.75] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        className="text-xs sm:text-sm text-amber-200/90 italic font-['Cormorant_Garamond',serif] text-center mt-1 select-none"
      >
        "Girasoles, tulipanes dorados y lirios floreciendo para ti con amor eterno."
      </motion.p>
    </div>
  );
};
